/**
 * createGatewayClientFromShellSession — factory that builds a gateway search
 * client from the current shell session and the configured gateway base URL.
 *
 * Returns null when:
 *   - no active (valid, non-expired) shell session exists
 *   - session is missing gatewayAccessToken or gatewayToken
 *   - session is missing gatewayBaseUrl
 *
 * Gateway base URL source:
 *   session.gatewayBaseUrl — captured during browser-owned bootstrap from
 *   admin app runtime config, so this works in cross-origin iframes where
 *   parent window access is unavailable.
 *
 * Priority order at the call site (enforced by callers, not this module):
 *   1. Explicit `client` prop — highest priority
 *   2. Result of this factory — shell session path (self-hosted / iframe)
 *   3. wsClient — legacy cloud fallback
 *
 * The caller is responsible for loading the session once and passing it in.
 * This factory does NOT call sessionStorage internally — the provider owns
 * the single session read so context fields and the gateway client always
 * derive from the same snapshot.
 */

import { createGatewaySearchClient } from '../../search-settings/gateway/gatewaySearchClient';
import { isDevelopmentBuild } from '../../../util/environment';

/**
 * Rewrite Docker-internal hostnames to localhost so the browser can reach
 * the gateway when running in local dev.
 *
 * In a fullchain Docker setup the PS plugin stores the gateway URL as
 * http://host.docker.internal:PORT or http://kea-fullchain-gateway:PORT —
 * hostnames that are only resolvable inside Docker, not from the host browser.
 * Replacing them with localhost makes the URL accessible without changing any
 * Docker or PS DB configuration.
 *
 * Only active in development builds; production URLs are left untouched.
 *
 * @param {string} url
 * @returns {string}
 */
function normalizeGatewayUrl(url) {
  if (!isDevelopmentBuild) return url;
  return url
    .replace(/\bhost\.docker\.internal\b/g, 'localhost')
    .replace(/\bkea-fullchain-gateway\b/g, 'localhost');
}

/**
 * Read the gateway base URL.
 *
 * Source: session.gatewayBaseUrl (cross-origin safe, stored at bootstrap time).
 *
 * @param {object} session - Loaded shell session object
 * @returns {string|null}
 */
function resolveBaseUrl(session) {
  if (session?.gatewayBaseUrl && typeof session.gatewayBaseUrl === 'string') {
    return normalizeGatewayUrl(session.gatewayBaseUrl);
  }
  return null;
}

/**
 * Build a gateway search client from a pre-loaded shell session.
 *
 * @param {object|null} session - Pre-loaded shell session (the caller owns the
 *   single sessionStorage read). Pass null when no valid session is present.
 * @returns {object|null} Gateway search client, or null if prerequisites are missing.
 */
export function createGatewayClientFromShellSession(session) {
  if (!session) return null;
  if (!session.gatewayAccessToken) return null;
  if (!session.gatewayToken) return null;

  const baseUrl = resolveBaseUrl(session);
  if (!baseUrl) return null;

  return createGatewaySearchClient({
    baseUrl,
    token: session.gatewayToken,
    authToken: session.gatewayAccessToken,
  });
}
